import { apiService } from '@/service/api'
import type { DigitalCoworkerRecord } from '@/service/api'

/**
 * The one place a digital coworker's avatar URL is built (FS-702).
 *
 * The avatar is served from `v1/digital-coworkers/{id}/avatar`, a path that
 * does not change when the photo behind it does. Replacing an avatar therefore
 * produced a byte-identical URL and the browser kept serving the image it had
 * already cached — the new photo only appeared after a hard reload.
 *
 * `updated_at` is the version: the avatar write goes through
 * `coworkerRepo.update(...)`, which bumps the entity's `@UpdateDateColumn`, so
 * the timestamp moves whenever the photo does.
 *
 * Seven call sites each rebuilt this expression by hand, which is why the
 * caching bug could not be fixed in one place. They all route through here now.
 *
 * A `Cache-Control`/`ETag` on the endpoint would be the better fix and would
 * make the query parameter unnecessary — see FS-702.
 */

/** The fields the URL is derived from — anything carrying them will do. */
export type CoworkerAvatarSource = Pick<
  DigitalCoworkerRecord,
  'avatar_photo_url' | 'photo_url' | 'updated_at'
>

/**
 * @returns the resolved, cache-busted avatar URL, or null when the coworker has
 *          no photo — callers render their initials fallback on null.
 */
export function coworkerAvatarUrl(
  coworker: CoworkerAvatarSource | null | undefined,
): string | null {
  // `photo_url` is the original upload, `avatar_photo_url` the generated
  // illustration; the generated one wins where both exist.
  const path = coworker?.avatar_photo_url ?? coworker?.photo_url
  if (!path) return null

  const url = apiService.getUrl(path)
  const version = coworker?.updated_at
  // No timestamp means nothing to key the cache on — a bare URL beats a
  // `v=undefined` that would itself be cached under the wrong key.
  if (!version) return url

  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`
}
